import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"; // React primitives used to store and expose shared state.
import { TenantUserRole } from "../types"; // Shared type that keeps role strings aligned with the backend enum.
import { useMsal } from "@azure/msal-react"; // Hook that exposes the MSAL instance + cached accounts.
import { getAccessToken } from "../services/authService"; // Helper that mints backend-scoped tokens via MSAL.
import {
  fetchRegistrationAuthorisation,
  RegistrationForbiddenError,
} from "../services/registrationService"; // Calls the backend registration gate and exposes a domain-specific 403 error.
import { warmupWorker } from "../services/jobService"; // Best-effort worker warmup to reduce "pending" time after submit.

export type RegistrationStatus = "idle" | "loading" | "authorised" | "unauthorised" | "error"; // Phases the UI can branch on (spinner vs. blocked vs. normal flow).

export interface RegistrationState {
  status: RegistrationStatus; // Whether we are checking, allowed, blocked, or in an error state.
  roles: TenantUserRole[]; // Role flags granted by the backend (controls which flows/pages are enabled).
  errorMessage?: string; // Optional copy shown when we block the user.
}

const initialState: RegistrationState = {
  status: "idle", // We have not contacted the backend yet.
  roles: [], // Until the backend responds we assume zero roles are granted.
};

interface RegistrationContextValue {
  state: RegistrationState; // Snapshot of the current registration status.
  refresh: () => Promise<void>; // Async refresher so callers can re-run the gate manually.
}

const RegistrationContext = createContext<RegistrationContextValue | undefined>(undefined); // Defensively initialise with undefined so misuse throws.

export const RegistrationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [registrationState, setRegistrationState] = useState<RegistrationState>(initialState); // Keep the gate status local to this component tree.

  const { instance: msalInstance, accounts } = useMsal(); // Grab the MSAL instance + currently signed-in accounts.
  const accessTokenRef = useRef<string | null>(null); // Cache the backend access token so repeated refreshes avoid extra MSAL calls.

  const ensureAccessToken = useCallback(async () => {
    if (accessTokenRef.current) {
      return accessTokenRef.current; // Fast path: reuse the cached token.
    }
    if (!accounts.length) {
      throw new Error("No active MSAL account found; please sign in again."); // Should not happen once login completes.
    }
    const token = await getAccessToken(msalInstance); // Ask MSAL for the backend-scoped token.
    accessTokenRef.current = token; // Cache it for subsequent calls.
    return token;
  }, [accounts, msalInstance]);

  const refresh = useCallback(async () => {
    setRegistrationState({ status: "loading", roles: [] }); // Let the UI show a spinner immediately.
    try {
      const accessToken = await ensureAccessToken(); // Lazily fetch (or reuse) the backend token.
      const { authorised, roles } = await fetchRegistrationAuthorisation(accessToken); // Call the backend registration gate.
      if (authorised) {
        setRegistrationState({ status: "authorised", roles }); // Happy path: we’re authorised, cache the roles.
        // Best-effort optimisation: kick the worker so it can spin up before the user submits a job.
        // This is intentionally fire-and-forget and should not block login UX.
        warmupWorker(accessToken).catch(() => undefined);
        return;
      }
      setRegistrationState({
        status: "unauthorised",
        roles: [],
        errorMessage:
          "You are not authorised to use this service. Contact your administrator to request access.",
      }); // Backend explicitly denied access (rare because the API normally raises 403 first).
    } catch (error) {
      const denied = error instanceof RegistrationForbiddenError; // Distinguish between expected 403 vs. unexpected failure.
      const fallbackMessage =
        "You are not authorised to use this service. Contact your administrator to request access.";
      const message = (error as Error).message ?? fallbackMessage; // Prefer the backend-provided message but keep a fallback.
      setRegistrationState({
        status: denied ? "unauthorised" : "error",
        roles: [],
        errorMessage: message,
      });
      if (denied) {
        console.warn("Registration check denied:", error);
      } else {
        console.error("Registration check failed:", error);
      }
    }
  }, [ensureAccessToken]);

  useEffect(() => {
    if (!accounts.length) {
      return; // No signed-in account -> nothing to check yet.
    }
    refresh().catch((error) => console.error("Initial registration check failed:", error)); // Fire the gate as soon as the user signs in.
  }, [accounts, refresh]);

  return (
    <RegistrationContext.Provider value={{ state: registrationState, refresh }}>
      {children}
    </RegistrationContext.Provider>
  );
};

export const useRegistration = () => {
  const context = useContext(RegistrationContext); // Read the nearest provider.
  if (!context) {
    throw new Error("useRegistration must be used within a RegistrationProvider"); // Fail fast if the provider was not mounted.
  }
  return context; // Consumers receive `{ registrationState, refresh }`.
};
